import { Check } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { focusRing } from '../lib/focus'

export interface StepDefinition {
  id: string
  label: string
  /** Short description shown under the label on wide viewports. */
  description?: string
  /** Set once the step has been visited and validated. */
  complete?: boolean
  /** Blocks navigation to this step — used sparingly; see the note below. */
  disabled?: boolean
}

export interface StepperProps {
  steps: StepDefinition[]
  currentStepId: string
  onStepChange: (id: string) => void
  label: string
  orientation?: 'horizontal' | 'vertical'
  className?: string
  children?: ReactNode
}

/**
 * F0-17 — wizard navigation for BB-FR-001, the TOSCA wizard (BB-FR-013) and the
 * Scale Decision Wizard (MSE-FR-008).
 *
 * BB-FR-001 requires free back-navigation with all state preserved, so every
 * *visited* step is an enabled button, not a breadcrumb. Steps are never
 * disabled merely because they are "ahead" — that is what turns a 5-step wizard
 * into a 10-minute AC-1 failure. `disabled` exists only for steps that are
 * genuinely unreachable (e.g. a methodology-specific step for another design).
 *
 * Rendered as an ordered list so screen readers announce "step 2 of 5" from
 * structure rather than from a decorative string.
 */
export function Stepper({
  steps,
  currentStepId,
  onStepChange,
  label,
  orientation = 'horizontal',
  className,
  children,
}: StepperProps) {
  const currentIndex = steps.findIndex((s) => s.id === currentStepId)

  return (
    <nav aria-label={label} className={className}>
      <ol
        className={cn(
          'flex gap-2',
          orientation === 'horizontal' ? 'flex-col sm:flex-row sm:items-start' : 'flex-col',
        )}
      >
        {steps.map((step, index) => {
          const isCurrent = step.id === currentStepId
          const isComplete = step.complete ?? index < currentIndex
          const isReachable = !step.disabled && (isComplete || index <= currentIndex)

          return (
            <li key={step.id} className={cn('flex-1', orientation === 'horizontal' && 'min-w-0')}>
              <button
                type="button"
                onClick={() => isReachable && onStepChange(step.id)}
                disabled={!isReachable}
                aria-current={isCurrent ? 'step' : undefined}
                className={cn(
                  'group flex w-full items-start gap-3 rounded-md p-3 text-start transition-colors duration-fast',
                  isReachable ? 'hover:bg-surface-hover' : 'cursor-not-allowed opacity-60',
                  isCurrent && 'bg-surface-selected',
                  focusRing,
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'flex size-6 shrink-0 items-center justify-center rounded-full border text-caption font-semibold',
                    isComplete && 'border-state-verified-border bg-state-verified-surface text-state-verified',
                    isCurrent && !isComplete && 'border-brand-solid bg-brand-solid text-text-on-brand',
                    !isCurrent && !isComplete && 'border-border-control text-text-secondary',
                  )}
                >
                  {isComplete ? <Check className="size-3.5" /> : index + 1}
                </span>

                <span className="flex min-w-0 flex-col">
                  <span className="flex items-center gap-2">
                    <span className="text-micro uppercase tracking-wide text-text-tertiary">
                      Step {index + 1} of {steps.length}
                    </span>
                    {isComplete && <span className="sr-only">(completed)</span>}
                  </span>
                  <span
                    className={cn(
                      'truncate text-body-sm font-medium',
                      isCurrent ? 'text-text-primary' : 'text-text-secondary',
                    )}
                  >
                    {step.label}
                  </span>
                  {step.description && (
                    <span className="hidden text-caption text-text-tertiary lg:block">
                      {step.description}
                    </span>
                  )}
                </span>
              </button>
            </li>
          )
        })}
      </ol>
      {children}
    </nav>
  )
}
